import { Button, Input, Modal, Select, StatCard, Surface, Textarea } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SalesOrderListRow, StaffOrderStatus } from '@warehouse/shared';
import { ClipboardCheck, PackageCheck, Plus, UserCheck } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { usePermission } from '../../../auth/auth-store';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { ORDER_QUERY_KEYS, portalApi, staffOrdersApi } from '../api';
import { OrderLineEditor, type EditableLine } from '../components/OrderLineEditor';
import { StaffStatusBadge } from '../components/StatusBadge';

/**
 * Staff orders (spec §5.14). A separate list from customer orders because they
 * are a different job: no delivery date to hit, no shipment to book, just
 * "registrera → gör klart → någon hämtar och skriver under".
 */
export function StaffOrdersPage(): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canCreate = usePermission('orders.staff.create');

  const { state, setState, setFilter } = useListState({ defaultSort: 'createdAt', defaultDir: 'desc' });
  const [creating, setCreating] = useState(false);

  const listQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.staffList(state),
    queryFn: () => staffOrdersApi.list(state),
  });
  const statsQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.staffStats(),
    queryFn: () => staffOrdersApi.stats(),
  });

  useLiveQuery(['sales-orders'], ['staff-orders']);

  const columns = useMemo<DataTableColumn<SalesOrderListRow>[]>(
    () => [
      {
        id: 'orderNo',
        header: t('salesOrders:staff.columns.orderNo'),
        sortField: 'orderNo',
        cell: (row) => <span className="font-mono text-xs">{row.orderNo}</span>,
      },
      {
        id: 'status',
        header: t('salesOrders:staff.columns.status'),
        cell: (row) => <StaffStatusBadge status={(row.staffStatus ?? 'registered') as StaffOrderStatus} />,
      },
      {
        id: 'unit',
        header: t('salesOrders:staff.columns.unit'),
        sortField: 'restaurant',
        cell: (row) => row.restaurantName,
      },
      {
        id: 'orderDate',
        header: t('salesOrders:staff.columns.orderDate'),
        sortField: 'orderDate',
        cell: (row) => row.orderDate,
      },
      {
        id: 'lines',
        header: t('salesOrders:staff.columns.lines'),
        align: 'right',
        cell: (row) => row.lineCount,
        hideBelow: 'md',
      },
      {
        id: 'collectedBy',
        header: t('salesOrders:staff.columns.collectedBy'),
        cell: (row) => row.collectedBy ?? '—',
        hideBelow: 'lg',
      },
      {
        id: 'collectedAt',
        header: t('salesOrders:staff.columns.collectedAt'),
        cell: (row) => (row.collectedAt ? new Date(row.collectedAt).toLocaleString('sv-SE') : '—'),
        hideBelow: 'xl',
      },
    ],
    [t],
  );

  const stats = statsQuery.data;

  return (
    <>
      <PageHeader
        title={t('salesOrders:staff.title')}
        subtitle={t('salesOrders:staff.subtitle')}
        actions={
          canCreate ? (
            <Button
              icon={<Plus className="size-4" aria-hidden />}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('salesOrders:staff.actions.new')}
            </Button>
          ) : null
        }
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={<ClipboardCheck className="size-5" aria-hidden />}
          label={t('salesOrders:staff.stats.total')}
          value={stats?.total ?? 0}
          loading={statsQuery.isLoading}
        />
        <StatCard
          icon={<ClipboardCheck className="size-5" aria-hidden />}
          label={t('salesOrders:staff.stats.registered')}
          value={stats?.byStatus.registered ?? 0}
          loading={statsQuery.isLoading}
        />
        <StatCard
          icon={<PackageCheck className="size-5" aria-hidden />}
          label={t('salesOrders:staff.stats.ready')}
          value={stats?.byStatus.ready ?? 0}
          loading={statsQuery.isLoading}
        />
        <StatCard
          icon={<UserCheck className="size-5" aria-hidden />}
          label={t('salesOrders:staff.stats.collected')}
          value={stats?.byStatus.collected ?? 0}
          loading={statsQuery.isLoading}
        />
      </div>

      <DataTable
        columns={columns}
        rows={listQuery.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={listQuery.data?.total ?? null}
        loading={listQuery.isLoading}
        error={listQuery.error}
        searchPlaceholder={t('salesOrders:list.searchPlaceholder')}
        emptyTitle={t('salesOrders:list.empty')}
        onRowClick={(row) => {
          void navigate(`/orders/staff/${row.id}`);
        }}
        filters={
          <Select
            aria-label={t('salesOrders:staff.columns.status')}
            value={(state.filters?.staffStatus as string) ?? ''}
            onChange={(event) => {
              setFilter('staffStatus', event.target.value || undefined);
            }}
            options={[
              { value: '', label: t('salesOrders:list.filters.allStatuses') },
              ...(['registered', 'ready', 'collected'] as StaffOrderStatus[]).map((status) => ({
                value: status,
                label: t(`salesOrders:staff.status.${status}`),
              })),
            ]}
          />
        }
      />

      {stats && stats.topArticles.length > 0 ? (
        <Surface className="mt-4 p-5">
          <h2 className="mb-3 font-medium">{t('salesOrders:staff.stats.topArticles')}</h2>
          <ul className="flex flex-col gap-1 text-sm">
            {stats.topArticles.map((article) => (
              <li key={article.articleId} className="flex items-baseline justify-between gap-3">
                <span>
                  <span className="font-mono text-xs text-ink-muted">{article.articleNo}</span>
                  <span className="ml-2">{article.articleName}</span>
                </span>
                <span className="text-ink-muted">
                  {article.qtyBase} · {article.orders} {t('salesOrders:staff.stats.orders')}
                </span>
              </li>
            ))}
          </ul>
        </Surface>
      ) : null}

      {creating ? (
        <CreateStaffOrderModal
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(id) => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['staff-orders'] });
            void navigate(`/orders/staff/${id}`);
          }}
        />
      ) : null}
    </>
  );
}

function CreateStaffOrderModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const [restaurantId, setRestaurantId] = useState('');
  const [collectedBy, setCollectedBy] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<EditableLine[]>([]);

  const restaurantsQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.restaurants(),
    queryFn: () => portalApi.restaurants(),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      staffOrdersApi.create({
        restaurantId,
        ...(collectedBy.trim() ? { collectedBy: collectedBy.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        lines: lines.map((l) => ({ articleId: l.articleId, qty: l.qty, ...(l.unitId ? { unitId: l.unitId } : {}) })),
      }),
    onSuccess: (order) => {
      toastSuccess(t('salesOrders:staff.create.created', { orderNo: order.orderNo }));
      onCreated(order.id);
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('salesOrders:staff.create.title')} width={640}>
      <div className="flex flex-col gap-4">
        <Select
          label={t('salesOrders:staff.create.unit')}
          placeholder={t('salesOrders:create.restaurantPlaceholder')}
          value={restaurantId}
          onChange={(event) => {
            setRestaurantId(event.target.value);
          }}
          options={(restaurantsQuery.data?.items ?? []).map((r) => ({ value: r.id, label: r.name }))}
        />

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('salesOrders:staff.create.collectedBy')}</span>
          <Input
            value={collectedBy}
            placeholder={t('salesOrders:staff.create.collectedByPlaceholder')}
            onChange={(event) => {
              setCollectedBy(event.target.value);
            }}
          />
        </label>

        <div>
          <p className="mb-2 text-sm font-medium">{t('salesOrders:detail.linesTitle')}</p>
          <OrderLineEditor lines={lines} onChange={setLines} />
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('salesOrders:staff.create.note')}</span>
          <Textarea
            rows={2}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={!restaurantId || lines.length === 0}
            loading={createMutation.isPending}
            onClick={() => {
              createMutation.mutate(undefined);
            }}
          >
            {t('salesOrders:staff.create.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
